/**
 * Platform Health Dashboard
 * =========================
 * Dedicated page answering "why aren't we trading?" with full detail.
 * Accessible at /health in the sidebar.
 *
 * Combines:
 *  - TradingHealth snapshot (why-no-trades)
 *  - Deploy tier distribution
 *  - War Room freshness
 *  - Circuit breaker
 *  - Scan loop
 *  - Intraday regime
 *  - Deployed SHA (from /health endpoint)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  AlertTriangle,
  Bot,
  CheckCircle2,
  ChevronRight,
  Clock,
  GitCommit,
  RefreshCw,
  ShieldAlert,
  XCircle,
  Zap,
} from 'lucide-react';
import { titanApi, TradingHealth } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

// ── helpers ───────────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<string, string> = {
  ready:    'text-emerald-600',
  degraded: 'text-amber-600',
  blocked:  'text-red-600',
  closed:   'text-muted-foreground',
  error:    'text-red-600',
};

const STATUS_BG: Record<string, string> = {
  ready:    'border-emerald-500/30 bg-emerald-500/10',
  degraded: 'border-amber-500/30 bg-amber-500/10',
  blocked:  'border-red-500/30 bg-red-500/10',
  closed:   'border-muted bg-muted/20',
  error:    'border-red-500/30 bg-red-500/10',
};

const STATUS_ICON: Record<string, React.ElementType> = {
  ready:    CheckCircle2,
  degraded: AlertTriangle,
  blocked:  XCircle,
  closed:   Clock,
  error:    XCircle,
};

function SectionCard({
  title,
  icon: Icon,
  children,
  className = '',
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-xl border bg-card p-5 space-y-3 ${className}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function TierPill({
  count,
  label,
  color,
}: {
  count: number;
  label: string;
  color: string;
}) {
  if (count === 0) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold ${color}`}>
      {count} <span className="font-normal">{label}</span>
    </span>
  );
}

// ── component ─────────────────────────────────────────────────────────────────

const PlatformHealth: React.FC = () => {
  const [health, setHealth] = useState<TradingHealth | null>(null);
  const [deployedSha, setDeployedSha] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [h, sha] = await Promise.allSettled([
        titanApi.whyNoTrades(),
        (titanApi as any).health().then((d: any) => d?.git_sha ?? null),
      ]);
      if (h.status === 'fulfilled') setHealth(h.value);
      if (sha.status === 'fulfilled') setDeployedSha(sha.value);
      setLastFetch(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 30_000);
    return () => clearInterval(id);
  }, [fetchAll]);

  const StatusIcon = health ? (STATUS_ICON[health.status] ?? AlertTriangle) : Activity;
  const tiers = health?.deploy_tiers;
  const totalBots = tiers ? Object.values(tiers).reduce((a, b) => a + b, 0) : 0;
  const shadowBlocked = tiers ? (tiers.shadow + tiers.quarantine) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
            <Activity className="w-6 h-6 text-primary" />
            Platform Health
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time diagnostic — why is (or isn't) the platform trading?
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAll}
          disabled={loading}
          className="flex items-center gap-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Top status banner */}
      {health && (
        <div className={`rounded-xl border p-5 flex items-start gap-4 ${STATUS_BG[health.status] ?? ''}`}>
          <StatusIcon className={`w-8 h-8 flex-shrink-0 mt-0.5 ${STATUS_COLOR[health.status] ?? ''}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-bold text-lg ${STATUS_COLOR[health.status] ?? ''}`}>
                {health.status.toUpperCase()}
              </span>
              {health.intraday_regime && (
                <Badge variant="outline" className="text-xs capitalize">
                  {health.intraday_regime.regime.replace(/_/g, ' ')}
                </Badge>
              )}
            </div>
            <p className="text-sm mt-1 font-medium">{health.top_reason}</p>
            {health.issues.length > 1 && (
              <ul className="mt-2 space-y-0.5">
                {health.issues.slice(1).map((issue, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <ChevronRight className="w-3 h-3 flex-shrink-0 mt-0.5" />
                    {issue}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Grid of detail cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

        {/* Deploy tiers */}
        <SectionCard title="Bot Deploy Tiers" icon={Bot}>
          {tiers ? (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                <TierPill count={tiers.live}       label="live"       color="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" />
                <TierPill count={tiers.scout}      label="scout"      color="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" />
                <TierPill count={tiers.shadow}     label="shadow"     color="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" />
                <TierPill count={tiers.quarantine} label="quarantine" color="bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" />
                <TierPill count={tiers.other}      label="other"      color="bg-muted text-muted-foreground" />
              </div>
              {shadowBlocked > 0 && (
                <p className={`text-xs flex items-center gap-1 ${shadowBlocked === totalBots ? 'text-red-600' : 'text-amber-600'}`}>
                  <ShieldAlert className="w-3 h-3" />
                  {shadowBlocked} bot{shadowBlocked !== 1 ? 's' : ''} cannot place orders
                </p>
              )}
              {shadowBlocked === 0 && totalBots > 0 && (
                <p className="text-xs text-emerald-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  All {totalBots} bots can trade
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
        </SectionCard>

        {/* War Room */}
        <SectionCard title="War Room Freshness" icon={Zap}>
          {health?.war_room ? (
            <div className="space-y-1">
              <p className="text-sm">
                {health.war_room.last_run_date
                  ? <>Last run: <strong>{health.war_room.last_run_date}</strong></>
                  : <span className="text-muted-foreground">Never run</span>}
              </p>
              {health.war_room.hours_since_run != null && (
                <p className="text-xs text-muted-foreground">
                  {health.war_room.hours_since_run}h ago
                </p>
              )}
              {health.war_room.stale ? (
                <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs">Stale — run War Room</Badge>
              ) : (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs">Fresh</Badge>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">—</p>
          )}
        </SectionCard>

        {/* Scan Loop */}
        <SectionCard title="Scan Loop" icon={Activity}>
          {health?.scan_loop ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${
                  !health.scan_loop.running ? 'bg-red-500'
                  : health.scan_loop.stale ? 'bg-amber-500 animate-pulse'
                  : 'bg-emerald-500 animate-pulse'
                }`} />
                <span className="text-sm font-medium">
                  {health.scan_loop.running ? 'Running' : 'Stopped'}
                </span>
              </div>
              {health.scan_loop.last_tick_seconds_ago != null && (
                <p className="text-xs text-muted-foreground">
                  Last tick: {health.scan_loop.last_tick_seconds_ago}s ago
                </p>
              )}
              {health.scan_loop.stale && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs">Stale loop</Badge>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Controller not running</p>
          )}
        </SectionCard>

        {/* Circuit Breaker */}
        <SectionCard title="Circuit Breaker" icon={ShieldAlert}>
          {health?.circuit_breaker ? (
            <div className="space-y-1">
              <p className="text-sm font-medium capitalize">
                {health.circuit_breaker.status}
              </p>
              {health.circuit_breaker.trip_reason && (
                <p className="text-xs text-muted-foreground">{health.circuit_breaker.trip_reason}</p>
              )}
              <Badge
                className={health.circuit_breaker.trading_allowed
                  ? 'bg-emerald-100 text-emerald-800 border-emerald-300 text-xs'
                  : 'bg-red-100 text-red-800 border-red-300 text-xs'}
              >
                {health.circuit_breaker.trading_allowed ? 'Trading allowed' : 'Trading BLOCKED'}
              </Badge>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">—</p>
          )}
        </SectionCard>

        {/* Intraday Regime */}
        <SectionCard title="Intraday Regime" icon={Clock}>
          {health?.intraday_regime ? (
            <div className="space-y-1">
              <p className="text-sm font-medium capitalize">
                {health.intraday_regime.regime.replace(/_/g, ' ')}
              </p>
              <Badge
                className={health.intraday_regime.trading_ok
                  ? 'bg-emerald-100 text-emerald-800 border-emerald-300 text-xs'
                  : 'bg-amber-100 text-amber-800 border-amber-300 text-xs'}
              >
                {health.intraday_regime.trading_ok ? 'Trading permitted' : 'Paused for regime'}
              </Badge>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">—</p>
          )}
        </SectionCard>

        {/* Deployed version */}
        <SectionCard title="Deployed Version" icon={GitCommit}>
          <div className="space-y-1">
            <p className="text-sm font-mono">
              SHA: <strong>{deployedSha ?? '—'}</strong>
            </p>
            {health?.generated_at && (
              <p className="text-xs text-muted-foreground">
                Health snapshot: {new Date(health.generated_at).toLocaleTimeString()}
              </p>
            )}
            {lastFetch && (
              <p className="text-xs text-muted-foreground/60">
                Refreshed: {lastFetch.toLocaleTimeString()}
              </p>
            )}
          </div>
        </SectionCard>
      </div>

      {/* Full issue list */}
      {health && health.issues.length > 0 && (
        <div className="rounded-xl border bg-card p-5 space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <h3 className="font-semibold text-sm">Active Issues ({health.issues.length})</h3>
          </div>
          <ul className="space-y-2">
            {health.issues.map((issue, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="font-bold text-muted-foreground flex-shrink-0 w-5 text-right">{i + 1}.</span>
                <span>{issue}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {health && health.issues.length === 0 && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          <span className="text-sm font-medium text-emerald-700">
            No active issues — platform is trading normally.
          </span>
        </div>
      )}
    </div>
  );
};

export default PlatformHealth;
